import React from 'react';

import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { FormattedMessage } from 'react-intl';

import AccommodationBadges from '@components/Accomodations';
import { Checkbox } from '@components/Base/Checkbox';
import IntegrityRating from '@components/Base/IntegrityRating';
import Tooltip from '@components/Base/NewToolTip';
import RatingAtom from '@components/Base/Rating/RatingAtom';
import { CustomColumnDef } from '@components/Base/Table/types';
import User from '@components/Base/UserDetails';
import { convertToTitleCase } from '@components/ProctorNgSessions/SessionTable/helper';
import { SessionData } from '@containers/candidates/types';
import { classNames } from '@utils/classNames';
import { RATING } from '@utils/data/enums';
import { formatDate, formatTimeFromMinutes } from '@utils/dateFormat';
import gripHorizontalIcon from 'app/images/grip-horizontal.svg';

import './styles.css';
import messages from '../messages';

export const createColumns = (): CustomColumnDef<SessionData>[] => [
  {
    id: 'Candidate Details',
    header: () => 'Candidate Details',
    accessorKey: 'candidate_details',
    enableSorting: false,
    hidden: false,
    cell: ({ row }) => {
      const email = row.original.candidate_details?.email || '';
      return (
        <User
          data={{
            name: email,
          }}
          size="sm"
          hideImage={false}
          additionalContent={row.original.candidate_id}
        />
      );
    },
    size: 280,
    className:
      'text-left text-sm font-medium font-roboto text-gray-900 attendee-position-left sticky attendee-column shadow-[inset_-1px_0px_0px_0px] shadow-gray-400/20',
    cellClassName:
      'text-left sticky attendee-position-left attendee-column shadow-[inset_-1px_0px_0px_0px] z-index-one shadow-gray-400/20',
  },
  {
    id: 'Status',
    header: () => 'Status',
    accessorKey: 'status',
    enableSorting: false,
    cell: ({ row }) => <div>{row.original.session_status}</div>,
    size: 230,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left text-sm/[19px] font-normal',
    hidden: false,
  },
  {
    id: 'Session Recording',
    header: () => 'Session Recording',
    accessorKey: 'session_recording',
    enableSorting: false,
    cell: () => (
      <div>
        <FormattedMessage {...messages.view_recording} />
      </div>
    ),
    size: 180,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: true,
  },
  {
    id: 'Session Rating',
    header: () => (
      <div className="flex align-middle">
        <span className="mr-1">
          <FormattedMessage {...messages.session_rating} />
        </span>
        <Tooltip refDiv={<InformationCircleIcon className="w-5 h-5 text-gray-400" />} position="bottom-right">
          <IntegrityRating />
        </Tooltip>
      </div>
    ),
    accessorKey: 'session_rating',
    enableSorting: false,
    cell: ({ row }) => {
      return (
        <div className="flex align-left">
          <RatingAtom
            {...(row.original.system_rating && {
              rating: row.original.system_rating as RATING,
            })}
            showStatusIcon={false}
          />
        </div>
      );
    },
    size: 148,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Disposition',
    header: () => 'Disposition',
    accessorKey: 'disposition',
    enableSorting: false,
    cell: ({ row }) => <div>{row.original.summary}</div>,
    size: 280,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Accommodations',
    header: () => 'Accommodations',
    accessorKey: 'accommodations',
    enableSorting: false,
    cell: ({ row }) => <AccommodationBadges accommodations={row.original.accommodation} />,
    size: 262,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left whitespace-nowrap',
    hidden: false,
  },
  {
    id: 'Booking ID',
    header: () => 'Booking ID',
    accessorKey: 'booking_id',
    enableSorting: false,
    cell: () => <div>-</div>,
    size: 230,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left text-sm/[19px] font-normal',
    hidden: true,
  },
  {
    id: 'Enrolled At',
    header: () => 'Enrolled At',
    accessorKey: 'enrolled_at',
    enableSorting: false,
    cell: () => <div>-</div>,
    size: 230,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left text-sm/[19px] font-normal',
    hidden: false,
  },
  {
    id: 'Scheduled For',
    header: () => 'Scheduled For',
    accessorKey: 'scheduled_for',
    enableSorting: false,
    cell: () => <div>-</div>,
    size: 230,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left text-sm/[19px] font-normal',
    hidden: false,
  },
  {
    id: 'Payment Details',
    header: () => 'Payment Details',
    accessorKey: 'payment_details',
    enableSorting: false,
    cell: () => <div>-</div>,
    size: 230,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left text-sm/[19px] font-normal',
    hidden: false,
  },
  {
    id: 'Attempts',
    header: () => 'Attempts',
    accessorKey: 'attempts',
    enableSorting: false,
    cell: ({ row }) => <div>{row.original.attempt_count ? row.original.attempt_count : '-'}</div>,
    size: 95,
    className: 'text-center text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-center',
    hidden: true,
  },
  {
    id: 'Completed At',
    header: () => 'Completed At',
    accessorKey: 'completed_at',
    enableSorting: false,
    cell: ({ row }) => (row.original.completed_at ? formatDate(new Date(row.original.completed_at), 'withTime') : '-'),
    size: 230,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left text-sm/[19px] font-normal',
    hidden: false,
  },
  {
    id: 'Session Duration',
    header: () => 'Session Duration',
    accessorKey: 'session_duration',
    enableSorting: false,
    cell: ({ row }) => (row.original.duration ? formatTimeFromMinutes(row.original.duration) : '-'),
    size: 140,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'menu',
    enableSorting: false,
    accessorKey: 'menu',
    size: 48,
    className: 'text-left right-0 sticky shadow-[inset_0px_0px_0px_-1px] shadow-gray-400/20',
    cellClassName: 'text-center right-0 sticky z-index-one shadow-[inset_0px_0px_0px_-1px] shadow-gray-400/20',
    header: ({ table }) => {
      return (
        <div>
          <Popover className="flex justify-end items-center pr-3">
            <PopoverButton className={classNames('focus:outline-none')}>
              <PlusCircleIcon
                className={classNames('text-black h-5 w-5 focus:outline-none')}
                aria-hidden="true"
                data-testid="hide-column"
              />
            </PopoverButton>
            <Transition>
              <PopoverPanel>
                <div
                  className="bg-white border-solid border-[1px] menu-list-width font-normal shadow-sm menu-list-max-height overflow-y-auto rounded-md px-4 absolute right-0 top-12"
                  data-testid="column-model"
                >
                  {table.getAllLeafColumns().map((column) => {
                    if (column.id === 'menu') {
                      return null;
                    }
                    return (
                      <div key={column.id} className="menu-list-height w-full flex items-center">
                        <span className="pr-2 w-6 h-6">
                          <img src={gripHorizontalIcon} alt="edit" className="w-full h-full" />
                        </span>
                        <Checkbox
                          label={
                            typeof column.columnDef.header === 'string'
                              ? column.columnDef.header
                              : convertToTitleCase(column.id)
                          }
                          id={column.id}
                          size="md"
                          checked={column.getIsVisible()}
                          onChange={column.getToggleVisibilityHandler()}
                          disabled={false}
                        />
                      </div>
                    );
                  })}
                </div>
              </PopoverPanel>
            </Transition>
          </Popover>
        </div>
      );
    },
  },
];
